import React, { Component } from 'react'
import { connect } from 'react-redux'
import querystring from 'querystring'
import slugify from 'slugg'
import personalEmailDomains from 'personal-email-domains'
import email from 'ianstormtaylor-email'
import debounce from 'debounce'
import title from 'to-title-case'
import * as errors from 'lib/utils/errors'
import { error } from '@segment/notify'
import Signup from '../components/Signup'
import { isStrongLead } from 'lib/lead-predictions'
import {
  signupPage,
  signedUp,
  leadCaptured,
  identifyCall,
  emailCaptured,
  signupNextClicked
} from 'analytics'
import fetch from 'lib/fetch'

// TODO: move these completely server-side or fetch them async
const { destination, enable, next, signupWorkspace, signupProject } = window.segment
const invite = signupWorkspace || signupProject
let inviteType = null

if (signupWorkspace) {
  inviteType = 'workspace'
} else if (signupProject) {
  inviteType = 'project'
}

class SignupApp extends Component {
  constructor (props) {
    super(props)

    const query = querystring.parse(props.location.search.slice(1))
    const plan = query.plan || 'developer'

    this.state = {
      plan: plan,
      traits: {}, // Clearbit traits
      isStrongLead: false, // Madkudu lead scoring
      currentStep: 'user',
      existingEmail: null, // Set to email address if user enters one that's already in use
      isCreatingUser: false,
      isValidatingEmail: false, // Validating that email isn't already in use
      formData: { // Controlled form state
        email: query.email
      },
      data: {} // Validated / submitted form data
    }

    this.onEmailChange = debounce(this.onEmailChange.bind(this), 300)
    this.onEmailBlur = this.onEmailBlur.bind(this)
    this.onCompanySubmit = this.onCompanySubmit.bind(this)
    this.onFormChange = this.onFormChange.bind(this)
    this.onUserSubmit = this.onUserSubmit.bind(this)
  }

  render () {
    return (
      <Signup
        invite={invite}
        inviteType={inviteType}
        enable={enable}
        {...this.state}
        onUserSubmit={this.onUserSubmit}
        onCompanySubmit={this.onCompanySubmit}
        onFormChange={this.onFormChange}
        onEmailBlur={this.onEmailBlur} />
    )
  }

  componentDidMount () {
    const { formData } = this.state

    // Fire email change for querystring email
    this.onEmailChange(formData.email)

    signupPage()
  }

  onCompanySubmit (data) {
    this.createUser(data)
    signupNextClicked(data.email, 'company info', data.is_company, this.state.plan)
  }

  onEmailChange (email) {
    if (!email) {
      return
    }

    this.validateEmail(email)
  }

  onEmailBlur (email) {
    this.identifyLead(email)
  }

  onFormChange (field, value) {
    // Save controlled form state
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [field]: value
      }
    }))

    if (field === 'email') {
      this.onEmailChange(value)
    }
  }

  onUserSubmit (data) {
    try {
      leadCaptured(data.email, 'signup form')
      signupNextClicked(data.email, 'user info', data.is_company, this.state.plan)
    } catch (err) {
      errors.track(err)
      // ignore (see https://sentry.io/segment/app-frontend/issues/170604232/)
    }

    if (invite) {
      this.createUser(data)
      return
    }

    if (!data.is_company) {
      this.createUser(data)
      return
    }

    this.setState({ data, currentStep: 'company' })
  }

  createUser (newData = {}) {
    const { traits, data: otherData, plan } = this.state

    this.setState({ isCreatingUser: true })

    // Merge submitted form data, new form data and clearbit data together
    const data = {
      ...traits,
      ...newData,
      ...otherData,
      suggestedId: window.analytics && window.analytics.user && window.analytics.user().id()
    }

    // db-node and workspace creation depend on name field
    data.name = `${data.first_name} ${data.last_name}`
    data.type = invite ? 'invite' : 'organic'
    const queryParams = querystring.parse(window.location.search)
    if (Object.keys(queryParams).length) data.queryParams = queryParams

    fetch('/api/users', {
      method: 'POST',
      body: JSON.stringify(data)
    }).then(res => {
      if (!res.ok) {
        throw new Error('Unable to create user')
      }
      return res.json()
    }).then(user => {
      // Make sure to delete password before it goes to Segment's analytics
      delete data.password

      // Don't create a workspace for an invited user
      if (invite) {
        return { user }
      }

      return createWorkspaceWithSlugFallbacks(user, plan).then((result) => {
        result.user = user
        return result
      })
    }).then(({ user, workspace }) => {
      signedUp(user, data)
      let redirect

      if (workspace) {
        redirect = `/${workspace.slug}/sources`
      } else {
        redirect = '/workspaces'
      }

      window.location = next || destination || redirect
    }).catch((err) => {
      error('Sorry, we could not create your account.')
      this.setState({ isCreatingUser: false })
      errors.track(err)
    })
  }

  validateEmail (email) {
    this.setState({
      existingEmail: null,
      isValidatingEmail: true
    })

    fetch(`/api/users?email=${encodeURIComponent(email)}`, {
      method: 'HEAD'
    }).then(res => {
      if (res.ok) {
        this.setState({ existingEmail: email })
      }
    })
    .catch((err) => {
      // We don't really care about errors here
      errors.track(err)
    })
    .then(() => {
      this.setState({ isValidatingEmail: false })
    })
  }

  identifyLead (email) {
    enrichLead(email).then(res => {
      const traits = res.traits

      if (!traits) {
        return
      }

      this.setState((prevState) => ({
        traits,
        formData: {
          ...prevState.formData,
          first_name: prevState.formData.first_name || traits.first_name,
          last_name: prevState.formData.last_name || traits.last_name,
          company_name: prevState.formData.company_name || traits.company_name,
          company_size: prevState.formData.company_size || calculateCompanySize(traits.company_size),
          role: prevState.formData.role || traits.role
        }
      }))

      isStrongLead(isStrongLead => this.setState({ isStrongLead }))
      identifyCall(email, traits)
      emailCaptured(email)
    })
    .catch((err) => {
      // Fail silently since it only enriches user data for marketing
      errors.track(err)
    })
  }
}

function enrichLead (email) {
  return fetch('/api/users/clearbit', {
    method: 'POST',
    body: JSON.stringify({ email })
  }).then(res => {
    if (!res.ok) {
      throw new Error('Unable to enrich lead')
    }
    return res.json()
  })
}

function generateWorkspaceInformation (user) {
  const workspaceBody = { billing: { email: user.email } }
  const { domain, local } = email.parse(user.email)
  const [ mainDomain ] = domain.split('.')
  let slugs

  if (personalEmailDomains.includes(domain)) {
    // example: hi@roland.codes
    workspaceBody.name = user.name // Roland Warmerdam
    slugs = [
      slugify(user.name), // roland-warmerdam
      slugify(local), // hi
      slugify(user.email) // hi-roland-codes
    ]
  } else {
    // example: roland@segment.com
    workspaceBody.name = title(mainDomain) // Segment
    slugs = [
      slugify(mainDomain), // segment
      slugify(`${mainDomain}-${local}`), // segment-roland
      slugify(user.email) // roland-segment-com
    ]
  }

  return { workspaceBody, slugs }
}

function createWorkspaceWithSlugFallbacks (user, plan) {
  const { workspaceBody, slugs } = generateWorkspaceInformation(user)

  return new Promise((resolve) => {
    const firstSlug = slugs.shift()
    create(workspaceBody, firstSlug)

    function create (body, slug) {
      body.slug = slug

      return fetch(`/api/workspaces?plan=${encodeURIComponent(plan)}`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).then(res => {
        if (!res.ok) {
          throw new Error('Unable to create workspace')
        }
        return res.json()
      }).then((workspace) => {
        // Workspace created
        resolve({ workspace })
      }).catch((err) => {
        // Slug in use, try next slug
        const nextSlug = slugs.shift()
        if (nextSlug) {
          create(body, nextSlug)
        } else {
          // No slugs available, carry on with no workspace
          resolve({ workspace: null })
          errors.track(err)
        }
      })
    }
  })
}

function calculateCompanySize (size) {
  if (size >= 0 && size < 5) {
    return '1-5 employees'
  } else if (size >= 5 && size < 10) {
    return '5-10 employees'
  } else if (size >= 10 && size < 50) {
    return '10-50 employees'
  } else if (size >= 50 && size < 100) {
    return '50-100 employees'
  } else if (size >= 100 && size < 1000) {
    return '100-1000 employees'
  } else if (size >= 1000) {
    return '1000+ employees'
  }

  return ''
}

function mapStateToProps () {
  return {}
}

function mapDispatchToProps () {
  return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(SignupApp)



// WEBPACK FOOTER //
// ./containers/SignupApp.js